import type { ReactElement } from 'react';
import {
  mcpRiskLevelColors,
  mcpRiskLevelLabels,
  type MCPRiskLevel,
} from '@kirin/api-client/contracts/mcp';
import { cn } from '@/lib/utils';

/** An AgentMesh verdict tag: dangerous, risky, clean, partial_scan, unscanned. */
export type AgentmeshTag = MCPRiskLevel;

/**
 * Pill tint + text per AgentMesh tag (KIR-5108). The dot carries AgentMesh's
 * own tag colour verbatim (mcpRiskLevelColors); the tint and text are a
 * readable, theme-aware rendition of the same hue. The label is always
 * rendered, so colour is never the only signal. Shared by the MCP Servers and
 * Extensions inventories (KIR-5208), so one verdict looks the same in both.
 */
const TAG_CLASSES: Record<AgentmeshTag, string> = {
  dangerous: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  risky:
    'bg-yellow-50 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300',
  clean: 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300',
  partial_scan:
    'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
  unscanned:
    'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
};

interface AgentmeshTagBadgeProps {
  tag: AgentmeshTag;
  size?: 'sm' | 'lg';
  className?: string;
  /** Test id of the colour dot. */
  dotTestId?: string;
}

/** AgentMesh verdict as a coloured pill: dot + label. */
export function AgentmeshTagBadge({
  tag,
  size = 'sm',
  className,
  dotTestId = 'agentmesh-tag-dot',
}: AgentmeshTagBadgeProps): ReactElement {
  return (
    <span
      data-risk-level={tag}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-semibold whitespace-nowrap',
        size === 'lg' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs',
        TAG_CLASSES[tag],
        className
      )}
    >
      <span
        aria-hidden="true"
        data-testid={dotTestId}
        className={cn('rounded-full', size === 'lg' ? 'size-2' : 'size-1.5')}
        style={{ backgroundColor: mcpRiskLevelColors[tag] }}
      />
      {mcpRiskLevelLabels[tag]}
    </span>
  );
}

AgentmeshTagBadge.displayName = 'AgentmeshTagBadge';
